import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { RktButtonDirective } from '@rocketfinancialcorp/rocket-ui/button';
import { RktFormComponent, formCheckboxGroup, formRadioGroup } from '@rocketfinancialcorp/rocket-ui/form';

import { LinxProfileType, PaymentFrequency, TransactionOccurrenceType } from '@shared/constants';
import { FormModel, LinxRequestFinancialAccount, LinxRequestFinancialAccountType } from '@shared/models';
import { systemTimeZone } from '@shared/utils';

@Component({
  selector: 'app-linx-template-financial-account',
  templateUrl: './linx-template-financial-account.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [RktButtonDirective, FormsModule, ReactiveFormsModule, RktFormComponent],
})
export class LinxTemplateFinancialAccountComponent implements OnInit {
  @Input() selectedFinancialAccountDetails?: LinxRequestFinancialAccount;

  @Input() customerProfileType?: LinxProfileType;

  @Output() continue = new EventEmitter<LinxRequestFinancialAccount>();

  financialAccountForm = new UntypedFormGroup({});

  formModel: FormModel<{
    financialAccountType?: LinxRequestFinancialAccountType;
    financialAccountSubType?: string;
    verifyFinancialAccount?: Record<string, boolean>;
    debitAuthorization?: Record<string, boolean>;
    occurrenceType?: TransactionOccurrenceType;
    paymentFrequency?: PaymentFrequency;
  }> = {};

  financialAccountFields: FormlyFieldConfig[] = [
    formRadioGroup({
      key: 'financialAccountType',
      label: '',
      props: {
        options: [
          { label: 'Use Existing Financial Account', value: 'EXISTING' },
          { label: 'Create New Financial Account', value: 'NEW' },
        ],
        required: true,
      },
      expressions: {
        hide: () => this.customerProfileType === 'NEW',
      },
    }),
    formRadioGroup({
      key: 'financialAccountSubType',
      label: 'Account Type',
      props: {
        options: [
          { label: 'Checking Account', value: 'CHECKING' },
          { label: 'Saving Account', value: 'SAVING' },
          { label: 'Card Account', value: 'CARD' },
        ],
        required: true,
      },
      expressions: {
        hide: (field: FormlyFieldConfig) => this.customerProfileType !== LinxProfileType.New && !field.model.financialAccountType,
      },
    }),
    formCheckboxGroup({
      key: 'verifyFinancialAccount',
      label: 'Do you want to verify this financial account?',
      props: {
        options: [{ value: 'verify', label: 'Yes, verify this account.' }],
      },
      expressions: {
        hide: (field: FormlyFieldConfig) => field.model.financialAccountType !== LinxRequestFinancialAccountType.EXISTING,
      },
    }),
  ];

  debitAuthorizationForm = new UntypedFormGroup({});

  debitAuthorizationField: FormlyFieldConfig[] = [
    formCheckboxGroup({
      key: 'debitAuthorization',
      label: '',
      props: {
        options: [{ value: 'Yes', label: 'Debit Authorization' }],
      },
    }),
    formRadioGroup({
      key: 'occurrenceType',
      label: 'Occurrence Type',
      className: 'linx-half-form-field',
      props: {
        options: [
          { label: 'Single', value: 'SINGLE' },
          { label: 'Recurring', value: 'RECURRING' },
        ],
        required: true,
      },
      expressions: {
        hide: (field: FormlyFieldConfig) => !field.model.debitAuthorization?.Yes,
      },
    }),
    formRadioGroup({
      key: 'paymentFrequency',
      label: 'Frequency',
      className: 'linx-half-form-field',
      props: {
        options: [
          { label: 'Daily', value: PaymentFrequency.DAILY },
          { label: 'Weekly', value: PaymentFrequency.WEEKLY },
          { label: 'Biweekly', value: PaymentFrequency.BIWEEKLY },
          { label: 'Monthly', value: PaymentFrequency.MONTHLY },
          { label: 'Quarterly', value: PaymentFrequency.QUARTERLY },
          { label: 'Yearly', value: PaymentFrequency.YEARLY },
        ],
        required: true,
      },
      expressions: {
        hide: (field: FormlyFieldConfig) => !field.model.debitAuthorization?.Yes || field.model?.occurrenceType === 'SINGLE',
      },
    }),
  ];

  ngOnInit(): void {
    if (this.selectedFinancialAccountDetails) {
      this.formModel = {
        financialAccountType: this.selectedFinancialAccountDetails.financialAccountType,
        financialAccountSubType: this.selectedFinancialAccountDetails.financialAccountSubType,
      };
    }
  }

  onClickContinue(): void {
    const {
      financialAccountType = LinxRequestFinancialAccountType.NEW,
      financialAccountSubType,
      debitAuthorization,
      occurrenceType,
      paymentFrequency,
      verifyFinancialAccount,
    } = this.formModel;

    const formData = { financialAccountType, financialAccountSubType } as LinxRequestFinancialAccount;

    if (verifyFinancialAccount && verifyFinancialAccount.verify) {
      formData.verifyFinancialAccount = verifyFinancialAccount.verify;
    }

    if (debitAuthorization?.Yes) {
      formData.debitAuthorization = {
        occurrenceType,
        paymentFrequency,
        timeZone: systemTimeZone,
        currency: 'USD',
      };
      formData.verifyFinancialAccount = true;
    }
    this.continue.emit(formData);
  }

  onClearBtnClick(): void {
    this.financialAccountForm.reset();
  }
}
